/**
 * Roadmap panel — the Journey / Learning surface inside the five-area shell.
 *
 * It reads the archetype and age band from the sealed Career DNA™ payload and
 * composes the roadmap from hand-written copy (see `features/roadmap`). It
 * scores nothing. When there is no Career DNA™ yet, it says so warmly and
 * offers a way forward instead of showing an empty box.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Bell, BellOff, Bookmark, BookmarkCheck, Cloud, CloudOff, Compass, Download,
  Flame, Loader2, Minus, Plus, RefreshCw, RotateCcw, Target, Timer,
} from "lucide-react";
import RoadmapTimeline from "@/components/roadmap/RoadmapTimeline";
import { useCareerDna } from "@/hooks/useCareerDna";
import { buildRoadmap, type RoadmapKind } from "@/features/roadmap/roadmaps";
import { BAND_LABEL } from "@/features/assessment-v2/band";
import { isSaved, removeRoadmap, saveRoadmap } from "@/lib/savedRoadmaps";
import {
  clearRoadmapProgress,
  completionByDay,
  currentStreak,
  doneMilestones,
  markMilestone,
  progressKey,
  summarise,
} from "@/lib/roadmapProgress";
import {
  clearRoadmapTime,
  formatDuration,
  logTime,
  secondsByMilestone,
  totalSeconds,
} from "@/lib/roadmapProgress";
import { DAY_LABEL, dismissReminder, goalStatus, saveGoal } from "@/lib/roadmapGoals";
import { pushRoadmapState, syncRoadmapState, type SyncStatus } from "@/lib/roadmapSync";
import { useAuth } from "@/contexts/AuthContext";
import { exportRoadmapPdf } from "@/lib/roadmapPdf";
import { toast } from "@/hooks/use-toast";

export default function RoadmapPanel({
  kind,
  /** Compact mode drops the intro paragraph — used inside the report. */
  compact,
}: {
  kind: RoadmapKind;
  compact?: boolean;
}) {
  const { data: dna, isLoading } = useCareerDna();
  const { user } = useAuth();
  const archetype = dna?.archetypePrimary ?? null;
  const band = dna?.band ?? "18plus";

  const roadmap = useMemo(
    () => (archetype ? buildRoadmap(kind, archetype, band) : null),
    [kind, archetype, band],
  );

  const [saved, setSaved] = useState(false);
  const [done, setDone] = useState<string[]>([]);
  const key = archetype ? progressKey(kind, archetype, band) : null;

  useEffect(() => {
    if (archetype) setSaved(isSaved(kind, archetype, band));
  }, [kind, archetype, band]);

  const [time, setTime] = useState<Record<string, number>>({});
  const [goalTick, setGoalTick] = useState(0);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>("idle");
  const [reminderOpen, setReminderOpen] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const refresh = (k: string) => {
    setDone(doneMilestones(k));
    setTime(secondsByMilestone(k));
    setGoalTick((n) => n + 1);
  };

  useEffect(() => {
    setDone(key ? doneMilestones(key) : []);
    setTime(key ? secondsByMilestone(key) : {});
  }, [key]);

  // Cross-device sync: pull + merge on sign-in, then keep the device in step.
  useEffect(() => {
    if (!user?.id || !key) return;
    let live = true;
    setSyncStatus("syncing");
    syncRoadmapState(user.id).then((merged) => {
      if (!live) return;
      setSyncStatus(merged ? "synced" : "error");
      if (merged) refresh(key);
    });
    return () => {
      live = false;
    };
  }, [user?.id, key]);

  const pushLater = () => {
    if (!user?.id) return;
    setSyncStatus("syncing");
    pushRoadmapState(user.id).then((ok) => setSyncStatus(ok ? "synced" : "error"));
  };


  if (isLoading) {
    return (
      <section className="calm-card p-6" aria-busy="true">
        <Loader2 className="h-5 w-5 animate-spin text-primary" aria-hidden="true" />
        <p className="mt-3 text-sm text-muted-foreground">Putting your roadmap together…</p>
      </section>
    );
  }

  // Warm empty state — no data is not an error.
  if (!roadmap || !archetype) {
    return (
      <section className="calm-card p-6">
        <p className="inline-flex items-center gap-1.5 rounded-full bg-primary/12 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.14em] text-primary">
          <Compass className="h-3 w-3" aria-hidden="true" />
          {kind === "journey" ? "Journey" : "Learning"}
        </p>
        <h2 className="mt-4 font-display text-xl font-bold leading-tight">
          Your roadmap is waiting on one thing.
        </h2>
        <p className="mt-2 max-w-prose text-sm leading-relaxed text-muted-foreground">
          Roadmaps are built from your Career DNA™ — the way you like to work. Finish the
          walk and yours appears here, with every step sized to fit your week.
        </p>
        <div className="mt-5 flex flex-wrap gap-2.5">
          <Link
            to="/start"
            className="inline-flex min-h-11 items-center gap-2 rounded-2xl bg-primary px-4 text-sm font-medium text-primary-foreground transition-transform hover:scale-[1.02] active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            Start your discovery
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
          <Link
            to="/careers"
            className="inline-flex min-h-11 items-center gap-2 rounded-2xl border border-border/60 px-4 text-sm font-medium text-foreground/90 transition-colors hover:border-primary/40 hover:bg-primary/8 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            Look around the careers first
          </Link>
        </div>
      </section>
    );
  }

  const toggleSave = () => {
    if (saved) {
      removeRoadmap(kind, archetype, band);
      setSaved(false);
      toast({ title: "Removed from your saved roadmaps" });
    } else {
      saveRoadmap(kind, archetype, band);
      setSaved(true);
      toast({ title: "Roadmap saved", description: "You'll find it in your report area." });
    }
  };

  const exportPdf = () => {
    const ok = exportRoadmapPdf(roadmap, dna?.displayName ?? null);
    if (!ok) {
      toast({
        title: "Pop-up blocked",
        description: "Allow pop-ups for this site, then try the export again.",
        variant: "destructive",
      });
    }
  };

  const progressId = key as string;
  const summary = summarise(progressId, roadmap.milestones.length);
  const history = completionByDay(progressId, 14);
  const streak = currentStreak(progressId);
  const busiest = Math.max(1, ...history.map((d) => d.count));

  const status = goalStatus(progressId);
  const spentTotal = totalSeconds(progressId);
  void goalTick; // status is recomputed whenever progress or goals change

  const logMilestoneTime = (milestoneId: string, seconds: number) => {
    logTime(progressId, milestoneId, seconds);
    refresh(progressId);
    pushLater();
  };

  const updateGoal = (patch: Parameters<typeof saveGoal>[1]) => {
    saveGoal(progressId, patch);
    setGoalTick((n) => n + 1);
    pushLater();
  };

  const closeReminder = () => {
    dismissReminder(progressId);
    setReminderOpen(false);
    setGoalTick((n) => n + 1);
  };

  const toggleDone = (milestoneId: string, next: boolean) => {
    markMilestone(progressId, milestoneId, next);
    setDone(doneMilestones(progressId));
    setGoalTick((n) => n + 1);
    pushLater();
    if (next && !saved) {
      saveRoadmap(kind, archetype, band);
      setSaved(true);
    }
    if (next && summary.done + 1 === summary.total) {
      toast({ title: "Roadmap complete", description: "Every step done. That's a real thing." });
    } else if (next) {
      toast({ title: "Step marked done" });
    }
  };

  const resetProgress = () => {
    clearRoadmapProgress(progressId);
    clearRoadmapTime(progressId);
    setDone([]);
    setTime({});
    setGoalTick((n) => n + 1);
    pushLater();
    toast({ title: "Progress cleared for this roadmap" });
  };


  return (
    <section className="space-y-4">
      <header className="calm-card p-6">
        <p className="inline-flex items-center gap-1.5 rounded-full bg-primary/12 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.14em] text-primary">
          <Compass className="h-3 w-3" aria-hidden="true" />
          {kind === "journey" ? "Journey roadmap" : "Learning roadmap"}
        </p>
        <h2 className="mt-4 font-display text-2xl font-bold leading-tight">{roadmap.title}</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          {archetype}
          {dna?.archetypeSecondary ? ` · with a ${dna.archetypeSecondary} streak` : ""} · ages{" "}
          {BAND_LABEL[band]}
          {dna?.isPreview ? " · preview data" : ""}
        </p>
        {!compact && (
          <p className="mt-3 max-w-prose text-sm leading-relaxed text-muted-foreground">
            {roadmap.intro}
          </p>
        )}

        {/* Progress — how far along, and how it's built up over time. */}
        <div className="mt-5 rounded-2xl bg-[hsl(var(--surface-2))] p-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="text-sm font-medium text-foreground">
              {summary.done} of {summary.total} steps done
            </p>
            <p className="text-xs text-muted-foreground">
              {streak > 0 && (
                <span className="mr-2 inline-flex items-center gap-1 text-primary">
                  <Flame className="h-3 w-3" aria-hidden="true" />
                  {streak}-day streak
                </span>
              )}
              {summary.lastDoneAt
                ? `Last step ${new Date(summary.lastDoneAt).toLocaleDateString()}`
                : "Nothing marked yet"}
            </p>
          </div>

          <div
            className="mt-3 h-2 overflow-hidden rounded-full bg-muted"
            role="progressbar"
            aria-valuenow={summary.percent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`${kind === "journey" ? "Journey" : "Learning"} roadmap progress`}
          >
            <div
              className="h-full rounded-full bg-primary transition-[width] duration-500 motion-reduce:transition-none"
              style={{ width: `${summary.percent}%` }}
            />
          </div>

          <div className="mt-4">
            <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
              Last 14 days
            </p>
            <ul className="mt-2 flex h-10 items-end gap-1" aria-hidden="true">
              {history.map((d) => (
                <li
                  key={d.day}
                  className={`flex-1 rounded-sm ${d.count > 0 ? "bg-primary" : "bg-muted"}`}
                  style={{ height: d.count > 0 ? `${(d.count / busiest) * 100}%` : "4px" }}
                />
              ))}
            </ul>
            <p className="sr-only">
              {history.filter((d) => d.count > 0).length === 0
                ? "No steps completed in the last 14 days."
                : history
                    .filter((d) => d.count > 0)
                    .map((d) => `${d.day}: ${d.count} step${d.count === 1 ? "" : "s"}`)
                    .join(", ")}
            </p>
          </div>
        </div>

        {/* Weekly goal — the learner's own commitment, and a nudge if the week is thin. */}
        <div className="mt-3 rounded-2xl bg-[hsl(var(--surface-2))] p-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground">
              <Target className="h-4 w-4 text-primary" aria-hidden="true" />
              This week: {status.steps} of {status.goal.weeklySteps} steps
              {status.goal.weeklyMinutes > 0
                ? ` · ${status.minutes} of ${status.goal.weeklyMinutes} min`
                : ""}
            </p>
            <button
              type="button"
              onClick={() => setSettingsOpen((o) => !o)}
              aria-expanded={settingsOpen}
              className="min-h-11 rounded-2xl px-2 text-xs font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              {settingsOpen ? "Done" : "Set your goal"}
            </button>
          </div>

          <div
            className="mt-3 h-2 overflow-hidden rounded-full bg-muted"
            role="progressbar"
            aria-valuenow={status.percent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Weekly goal progress"
          >
            <div
              className={`h-full rounded-full transition-[width] duration-500 motion-reduce:transition-none ${
                status.met ? "bg-emerald-400" : "bg-primary"
              }`}
              style={{ width: `${status.percent}%` }}
            />
          </div>

          <p className="mt-2 text-xs text-muted-foreground">
            {status.met
              ? "Target met. Anything else this week is a bonus."
              : `${status.daysLeft} day${status.daysLeft === 1 ? "" : "s"} left in the week.`}
            {spentTotal > 0 && (
              <span className="ml-2 inline-flex items-center gap-1">
                <Timer className="h-3 w-3" aria-hidden="true" />
                {formatDuration(spentTotal)} on this roadmap
              </span>
            )}
          </p>

          {status.reminderDue && reminderOpen && (
            <div
              role="status"
              className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-primary/30 bg-primary/8 p-3"
            >
              <p className="text-xs leading-relaxed text-foreground/90">
                {status.goal.weeklySteps - status.steps} step
                {status.goal.weeklySteps - status.steps === 1 ? "" : "s"} to go before the week
                closes. One small step counts.
              </p>
              <button
                type="button"
                onClick={closeReminder}
                className="min-h-11 rounded-2xl px-3 text-xs font-medium text-primary hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                Got it
              </button>
            </div>
          )}

          {settingsOpen && (
            <div className="mt-4 space-y-4 border-t border-border/50 pt-4">
              <div className="flex items-center justify-between gap-3">
                <span id={`${progressId}-steps`} className="text-sm text-foreground/90">
                  Steps per week
                </span>
                <span className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => updateGoal({ weeklySteps: status.goal.weeklySteps - 1 })}
                    disabled={status.goal.weeklySteps <= 1}
                    aria-label="Fewer steps per week"
                    className="flex h-11 w-11 items-center justify-center rounded-2xl border border-border/60 text-foreground/90 transition-colors hover:border-primary/40 hover:bg-primary/8 disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                  >
                    <Minus className="h-4 w-4" aria-hidden="true" />
                  </button>
                  <output
                    htmlFor={`${progressId}-steps`}
                    className="w-8 text-center text-sm font-semibold tabular-nums"
                  >
                    {status.goal.weeklySteps}
                  </output>
                  <button
                    type="button"
                    onClick={() => updateGoal({ weeklySteps: status.goal.weeklySteps + 1 })}
                    disabled={status.goal.weeklySteps >= 7}
                    aria-label="More steps per week"
                    className="flex h-11 w-11 items-center justify-center rounded-2xl border border-border/60 text-foreground/90 transition-colors hover:border-primary/40 hover:bg-primary/8 disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                  >
                    <Plus className="h-4 w-4" aria-hidden="true" />
                  </button>
                </span>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <label htmlFor={`${progressId}-minutes`} className="text-sm text-foreground/90">
                  Minutes per week (optional)
                </label>
                <select
                  id={`${progressId}-minutes`}
                  value={status.goal.weeklyMinutes}
                  onChange={(e) => updateGoal({ weeklyMinutes: Number(e.target.value) })}
                  className="min-h-11 rounded-2xl border border-border/60 bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  {[0, 30, 60, 120, 180, 300].map((m) => (
                    <option key={m} value={m}>
                      {m === 0 ? "No time target" : `${m} min`}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <label htmlFor={`${progressId}-day`} className="text-sm text-foreground/90">
                  Remind me on
                </label>
                <select
                  id={`${progressId}-day`}
                  value={status.goal.reminderDay}
                  onChange={(e) => updateGoal({ reminderDay: Number(e.target.value) })}
                  disabled={!status.goal.remindersOn}
                  className="min-h-11 rounded-2xl border border-border/60 bg-background px-3 text-sm text-foreground disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  {DAY_LABEL.map((d, i) => (
                    <option key={d} value={i}>
                      {d}
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="button"
                onClick={() => updateGoal({ remindersOn: !status.goal.remindersOn })}
                aria-pressed={status.goal.remindersOn}
                className="inline-flex min-h-11 items-center gap-2 rounded-2xl border border-border/60 px-4 text-sm font-medium text-foreground/90 transition-colors hover:border-primary/40 hover:bg-primary/8 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                {status.goal.remindersOn ? (
                  <Bell className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <BellOff className="h-4 w-4" aria-hidden="true" />
                )}
                {status.goal.remindersOn ? "Reminders on" : "Reminders off"}
              </button>
            </div>
          )}
        </div>

        {user?.id && (
          <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-muted-foreground" role="status">
            {syncStatus === "syncing" ? (
              <>
                <RefreshCw className="h-3 w-3 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                Syncing across your devices…
              </>
            ) : syncStatus === "error" ? (
              <>
                <CloudOff className="h-3 w-3" aria-hidden="true" />
                Saved on this device — we'll sync when the connection is back.
              </>
            ) : (
              <>
                <Cloud className="h-3 w-3" aria-hidden="true" />
                Synced to your account.
              </>
            )}
          </p>
        )}

        <div className="mt-5 flex flex-wrap gap-2.5">
          <button
            type="button"
            onClick={toggleSave}
            aria-pressed={saved}
            className="inline-flex min-h-11 items-center gap-2 rounded-2xl bg-primary px-4 text-sm font-medium text-primary-foreground transition-transform hover:scale-[1.02] active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            {saved ? (
              <BookmarkCheck className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Bookmark className="h-4 w-4" aria-hidden="true" />
            )}
            {saved ? "Saved" : "Save this roadmap"}
          </button>
          <button
            type="button"
            onClick={exportPdf}
            className="inline-flex min-h-11 items-center gap-2 rounded-2xl border border-border/60 px-4 text-sm font-medium text-foreground/90 transition-colors hover:border-primary/40 hover:bg-primary/8 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            <Download className="h-4 w-4" aria-hidden="true" />
            Export as PDF
          </button>
          {summary.done > 0 && (
            <button
              type="button"
              onClick={resetProgress}
              className="inline-flex min-h-11 items-center gap-2 rounded-2xl border border-border/60 px-4 text-sm font-medium text-muted-foreground transition-colors hover:border-primary/40 hover:bg-primary/8 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              <RotateCcw className="h-4 w-4" aria-hidden="true" />
              Reset progress
            </button>
          )}
        </div>
      </header>

      <RoadmapTimeline
        milestones={roadmap.milestones}
        doneIds={done}
        onToggleDone={toggleDone}
        timeSpent={time}
        onLogTime={logMilestoneTime}
      />
    </section>
  );
}

